import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const ShippingInfo = () => {
  return (
    <>
    <SEO 
      title="Shipping Information"
      description="Learn about House of Deepak's shipping policies, delivery times, shipping charges, and international shipping options. Fast and reliable delivery across India."
      keywords="shipping information, delivery policy, shipping charges, delivery time, international shipping, India delivery, House of Deepak shipping"
    />
    <div className="min-h-screen bg-cream pt-20 pb-16">
      <div className="section-padding">
        <div className="container-max">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Header */}
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-serif font-light text-deep-brown mb-6">
                Shipping Information
              </h1>
              <p className="text-lg text-warm-gray max-w-2xl mx-auto leading-relaxed">
                Fast, secure, and reliable delivery across India
              </p>
            </div>

            {/* Content */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-8 md:p-12 space-y-8">
                
                {/* Introduction */}
                <section>
                  <h2 className="text-2xl font-serif font-light text-deep-brown mb-4">
                    Delivering Style to Your Doorstep
                  </h2>
                  <p className="text-warm-gray leading-relaxed">
                    At House of Deepak, we understand that receiving your order quickly and safely is important to you. We've partnered with reliable shipping providers to ensure your purchases reach you in perfect condition and within the promised timeframe.
                  </p>
                </section>

                {/* Shipping Zones */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    1. Shipping Zones & Delivery Time
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-green-50 p-6 rounded-lg">
                      <h4 className="text-lg font-medium text-green-800 mb-3">Metro Cities</h4>
                      <div className="text-green-700 space-y-2">
                        <p><strong>Cities:</strong> Mumbai, Delhi, Bangalore, Chennai, Hyderabad, Kolkata, Pune, Ahmedabad</p>
                        <p><strong>Delivery Time:</strong> 2-3 business days</p>
                        <p><strong>Express Option:</strong> Next day delivery available</p>
                      </div>
                    </div>
                    <div className="bg-blue-50 p-6 rounded-lg">
                      <h4 className="text-lg font-medium text-blue-800 mb-3">Tier 1 & 2 Cities</h4>
                      <div className="text-blue-700 space-y-2">
                        <p><strong>Coverage:</strong> Major cities and state capitals</p>
                        <p><strong>Delivery Time:</strong> 3-5 business days</p>
                        <p><strong>Express Option:</strong> 2-3 days available</p>
                      </div>
                    </div>
                    <div className="bg-orange-50 p-6 rounded-lg">
                      <h4 className="text-lg font-medium text-orange-800 mb-3">Tier 3 Cities & Towns</h4>
                      <div className="text-orange-700 space-y-2">
                        <p><strong>Coverage:</strong> Smaller cities and towns</p>
                        <p><strong>Delivery Time:</strong> 5-7 business days</p>
                        <p><strong>Note:</strong> Some remote areas may take longer</p>
                      </div>
                    </div>
                    <div className="bg-purple-50 p-6 rounded-lg">
                      <h4 className="text-lg font-medium text-purple-800 mb-3">Remote Areas</h4>
                      <div className="text-purple-700 space-y-2">
                        <p><strong>Coverage:</strong> Rural and remote locations</p>
                        <p><strong>Delivery Time:</strong> 7-10 business days</p>
                        <p><strong>Note:</strong> Additional charges may apply</p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Shipping Costs */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    2. Shipping Costs
                  </h3>
                  <div className="space-y-4">
                    <div className="bg-cream p-6 rounded-lg">
                      <h4 className="text-lg font-medium text-deep-brown mb-3">Free Shipping Eligible</h4>
                      <ul className="text-warm-gray leading-relaxed space-y-1 list-disc list-inside">
                        <li>Orders above ₹999 (Pan India)</li>
                        <li>All prepaid orders above ₹799</li>
                        <li>Exchange orders (if original was defective)</li>
                        <li>Special promotional periods</li>
                      </ul>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-warm-gray/20">
                        <thead>
                          <tr className="bg-deep-brown text-white">
                            <th className="border border-warm-gray/20 p-3 text-left">Zone</th>
                            <th className="border border-warm-gray/20 p-3 text-left">Standard Shipping</th>
                            <th className="border border-warm-gray/20 p-3 text-left">Express Shipping</th>
                            <th className="border border-warm-gray/20 p-3 text-left">COD Charges</th>
                          </tr>
                        </thead>
                        <tbody className="text-warm-gray">
                          <tr>
                            <td className="border border-warm-gray/20 p-3">Metro Cities</td>
                            <td className="border border-warm-gray/20 p-3">₹99</td>
                            <td className="border border-warm-gray/20 p-3">₹199</td>
                            <td className="border border-warm-gray/20 p-3">₹49</td>
                          </tr>
                          <tr className="bg-cream/50">
                            <td className="border border-warm-gray/20 p-3">Tier 1 & 2 Cities</td>
                            <td className="border border-warm-gray/20 p-3">₹129</td>
                            <td className="border border-warm-gray/20 p-3">₹249</td>
                            <td className="border border-warm-gray/20 p-3">₹69</td>
                          </tr>
                          <tr>
                            <td className="border border-warm-gray/20 p-3">Tier 3 Cities</td>
                            <td className="border border-warm-gray/20 p-3">₹159</td>
                            <td className="border border-warm-gray/20 p-3">₹299</td>
                            <td className="border border-warm-gray/20 p-3">₹89</td>
                          </tr>
                          <tr className="bg-cream/50">
                            <td className="border border-warm-gray/20 p-3">Remote Areas</td>
                            <td className="border border-warm-gray/20 p-3">₹199</td>
                            <td className="border border-warm-gray/20 p-3">Not Available</td>
                            <td className="border border-warm-gray/20 p-3">₹129</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>

                {/* Payment Options */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    3. Payment & Shipping Options
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-deep-brown mb-3">Prepaid Orders</h4>
                      <ul className="text-warm-gray leading-relaxed space-y-1 list-disc list-inside">
                        <li>Credit/Debit Cards</li>
                        <li>Net Banking</li>
                        <li>UPI (Google Pay, PhonePe, Paytm)</li>
                        <li>Digital Wallets</li>
                        <li>EMI Options (3, 6, 9, 12 months)</li>
                      </ul>
                      <p className="text-sm text-green-600 mt-2">✓ Lower shipping charges for prepaid orders</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-deep-brown mb-3">Cash on Delivery (COD)</h4>
                      <ul className="text-warm-gray leading-relaxed space-y-1 list-disc list-inside">
                        <li>Available in most serviceable areas</li>
                        <li>Additional COD charges apply</li>
                        <li>Maximum order value: ₹50,000</li>
                        <li>Exact change preferred</li>
                        <li>Available for standard shipping only</li>
                      </ul>
                      <p className="text-sm text-orange-600 mt-2">⚠ Higher shipping charges for COD orders</p>
                    </div>
                  </div>
                </section>

                {/* Order Processing */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    4. Order Processing Timeline
                  </h3>
                  <div className="bg-cream p-6 rounded-lg">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-8 h-8 bg-deep-brown text-white rounded-full flex items-center justify-center text-sm font-medium">1</div>
                        <div>
                          <h4 className="font-medium text-deep-brown">Order Confirmation</h4>
                          <p className="text-warm-gray text-sm">Within 30 minutes of payment</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="w-8 h-8 bg-deep-brown text-white rounded-full flex items-center justify-center text-sm font-medium">2</div>
                        <div>
                          <h4 className="font-medium text-deep-brown">Order Processing</h4>
                          <p className="text-warm-gray text-sm">24-48 hours (quality check & packaging)</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="w-8 h-8 bg-deep-brown text-white rounded-full flex items-center justify-center text-sm font-medium">3</div>
                        <div>
                          <h4 className="font-medium text-deep-brown">Shipping</h4>
                          <p className="text-warm-gray text-sm">Order dispatched with tracking details</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="w-8 h-8 bg-deep-brown text-white rounded-full flex items-center justify-center text-sm font-medium">4</div>
                        <div>
                          <h4 className="font-medium text-deep-brown">Delivery</h4>
                          <p className="text-warm-gray text-sm">Based on your location zone</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Packaging */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    5. Packaging & Sustainability
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-deep-brown mb-3">Our Packaging Promise</h4>
                      <ul className="text-warm-gray leading-relaxed space-y-1 list-disc list-inside">
                        <li>Eco-friendly packaging materials</li>
                        <li>Secure packaging to prevent damage</li>
                        <li>Elegant presentation for gifting</li>
                        <li>Minimalist design reducing waste</li>
                        <li>Reusable packaging components</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-deep-brown mb-3">What's Included</h4>
                      <ul className="text-warm-gray leading-relaxed space-y-1 list-disc list-inside">
                        <li>Branded packaging box/bag</li>
                        <li>Care instructions card</li>
                        <li>Thank you note</li>
                        <li>Invoice and warranty details</li>
                        <li>Gift wrapping (on request)</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Tracking */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    6. Order Tracking
                  </h3>
                  <div className="space-y-4">
                    <p className="text-warm-gray leading-relaxed">
                      Stay updated on your order status with our comprehensive tracking system:
                    </p>
                    <div className="bg-cream p-6 rounded-lg">
                      <h4 className="font-medium text-deep-brown mb-3">How to Track Your Order</h4>
                      <ol className="text-warm-gray leading-relaxed space-y-2 list-decimal list-inside">
                        <li>Check your email for tracking details after dispatch</li>
                        <li>Use the tracking number on our website or courier partner's site</li>
                        <li>Log into your account for real-time updates</li>
                        <li>Get SMS notifications at key delivery milestones</li>
                      </ol>
                    </div>
                    <div>
                      <h4 className="font-medium text-deep-brown mb-2">Tracking Status Meanings:</h4>
                      <ul className="text-warm-gray leading-relaxed space-y-1 list-disc list-inside ml-4">
                        <li><strong>Order Confirmed:</strong> Payment received and order confirmed</li>
                        <li><strong>Processing:</strong> Order is being prepared and quality checked</li>
                        <li><strong>Shipped:</strong> Order dispatched from our warehouse</li>
                        <li><strong>In Transit:</strong> Order is on the way to your address</li>
                        <li><strong>Out for Delivery:</strong> Order is with delivery agent for final delivery</li>
                        <li><strong>Delivered:</strong> Order successfully delivered</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Special Services */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    7. Special Shipping Services
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 rounded-lg">
                      <h4 className="font-medium text-amber-900 mb-2">Gift Wrapping</h4>
                      <p className="text-amber-800 text-sm">Premium gift wrapping service available for ₹99</p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-2">Express Delivery</h4>
                      <p className="text-blue-800 text-sm">Next-day delivery available in metro cities</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg">
                      <h4 className="font-medium text-green-900 mb-2">Scheduled Delivery</h4>
                      <p className="text-green-800 text-sm">Choose your preferred delivery date and time slot</p>
                    </div>
                  </div>
                </section>

                {/* Important Notes */}
                <section>
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    8. Important Shipping Notes
                  </h3>
                  <div className="bg-orange-50 border-l-4 border-orange-400 p-6">
                    <h4 className="font-medium text-orange-800 mb-3">Please Note:</h4>
                    <ul className="text-orange-700 leading-relaxed space-y-1 list-disc list-inside">
                      <li>Delivery times are estimated and may vary due to unforeseen circumstances</li>
                      <li>Orders placed on weekends or holidays will be processed on the next business day</li>
                      <li>We don't deliver on Sundays and national holidays</li>
                      <li>For COD orders, please keep exact change ready</li>
                      <li>Someone must be available to receive the package; we don't leave packages unattended</li>
                      <li>Multiple delivery attempts will be made; after 3 attempts, package returns to warehouse</li>
                    </ul>
                  </div>
                </section>

                {/* Contact Information */}
                <section className="bg-cream p-6 rounded-lg">
                  <h3 className="text-xl font-serif text-deep-brown mb-3">
                    Shipping Support
                  </h3>
                  <p className="text-warm-gray leading-relaxed mb-3">
                    Have questions about your shipment? Our logistics team is here to help:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 text-warm-gray">
                    <div>
                      <h4 className="font-medium text-deep-brown mb-2">Contact Information:</h4>
                      <p><strong>Email:</strong> shipping@houseofdeepak.in</p>
                      <p><strong>Phone:</strong> +91 9167602861</p>
                      <p><strong>WhatsApp:</strong> +91 9167602861</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-deep-brown mb-2">Support Hours:</h4>
                      <p>Monday - Saturday: 9:00 AM - 8:00 PM</p>
                      <p>Sunday: 10:00 AM - 6:00 PM</p>
                      <p>Response time: Within 2 hours</p>
                    </div>
                  </div>
                </section>

              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ShippingInfo;